import { useEffect, useReducer, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useStyles } from "../../common/appStyles";
import { FormControl, InputLabel, List, ListItem, ListItemText, MenuItem, Paper, Select, TextField } from '@mui/material';
import { IRegionDetail } from '../../types/regions';
import { ICreateSchoolRequest, IUpdateSchoolRequest } from "../../types/schools";
import { GetRegionList } from '../../services/user-service';

const initialState = {
    id: 0,
    name: '',
    regionId: 0,
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        default:
            throw new Error();
    }
}

/**
 * @param props - All of the properites and functions School Detail page needs from the SchoolAdmin Page.
 * @returns - a fullscreen dialog which allows a user to create a new school or edit an exisiting one.
 * @remarks This page has a drop down to select {@link IRegionDetail | Region}, Type a school into a textfield
 * and enter multiple Email Domains for that school.
 */
export function SchoolDetail(props) {
    const { showDialog, titleText, selectedSchool, handleClose, handleSave } = props;
    const classes = useStyles();
    const [emailDomain, setEmailDomain] = useState("");
    const [tempDomainList, setTempDomainList] = useState([""]);
    const [regions, setRegions] = useState<IRegionDetail[]>([]);

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        id: selectedSchool.id,
        name: selectedSchool.name,
        regionId: selectedSchool.regionId,
    });
    const { id, name, regionId } = state;

    const getAllRegionsList = () => {
        GetRegionList()
            .then(response => {
                setRegions(response);
            })
            .catch((error) => {
                console.error(error);
            });
    }

    const setField = (field, value) => {
        dispatch({ type: 'SET_FIELD', field, value });
    }

    const handleAddEmailDomain = () => {
        setTempDomainList(prevTempDomainList => [...prevTempDomainList, emailDomain]);
    }

    const handleDeleteEmailDomain = (value: number) => {
        const newData = [...tempDomainList];
        newData.splice(value, 1);
        setTempDomainList(newData);
    }

    const handleSubmit = () => {
        const request = selectedSchool.id === 0 ? handleCreateSchool() : handleUpdateSchool();
        handleSave(request);
    }

    const handleUpdateSchool = () => {
        const request: IUpdateSchoolRequest = {
            id: id,
            name: name,
            emailDomainList: tempDomainList,
            regionId: regionId
        }
        return request;
    }

    const handleCreateSchool = () => {
        const request: ICreateSchoolRequest = {
            schoolName: name,
            schoolEmails: tempDomainList,
            regionId: regionId
        }
        return request;
    }

    useEffect(() => {
        setTempDomainList(selectedSchool.emailDomainList);
        getAllRegionsList();
    }, [selectedSchool]);

    const isFormValid =
        (
            name !== ("") &&
            tempDomainList.length !== 0 &&
            regionId !== (0)
        );

    const isEmailDomainValid =
        (
            emailDomain.trim().charAt(0) !== '.' &&
            emailDomain.includes(".")
        );

    return (
        <div>
            <Dialog
                open={showDialog}
                onClose={handleClose}
                maxWidth={"lg"}
                fullScreen
            >
                <AppBar sx={{ position: 'fixed' }}>
                    <Toolbar>
                        <Typography sx={{ flex: 1 }} variant="h5" component="div" aria-labelledby={titleText}>
                            {titleText}
                        </Typography>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Grid className={classes.wrapper} alignItems={"center"}>
                    <Grid
                        container
                        rowSpacing={6}
                        className={classes.supWrapper}
                        direction={"column"}
                        justifyContent={'flex-start'}
                        alignItems={"center"}
                    >
                        <Grid container direction={"row"} columnSpacing={4} alignItems={"center"} justifyContent={"center"} className={classes.fixGridMargin} >
                            <Grid item xs={3} height={"10rem"} minWidth={{ xs: '40vh', md: '5vh' }} >
                                <Paper elevation={6}>
                                    <Grid
                                        container
                                        direction={"row"}
                                        justifyContent="center"
                                        alignItems="center"
                                        className={classes.gridPadding}
                                        rowSpacing={3}
                                    >
                                        <Grid item xs={12} md={12} className={classes.gridPaddingTop}>
                                            <Grid className={classes.gridMargin}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Region</InputLabel>
                                                    <Select
                                                        labelId="region-label"
                                                        MenuProps={{
                                                            disableScrollLock: true,
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: "15vh",
                                                                },
                                                            }
                                                        }}
                                                        value={regionId}
                                                        label="Region"
                                                        onChange={(event) => {
                                                            setField('regionId', event.target.value);
                                                        }}
                                                    >
                                                        {regions.map((region) => (
                                                            <MenuItem key={region.id} value={region.id}>
                                                                {region.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={12} >
                                            <Grid className={classes.gridMargin}>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        value={name}
                                                        onChange={(event) => setField('name', event.target.value)}
                                                        label={"School"}
                                                        aria-labelledby={"school"}
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={12} >
                                            <Grid className={classes.gridMargin}>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        value={emailDomain}
                                                        onChange={(event) => setEmailDomain(event.target.value)}
                                                        label={"Email Domain"}
                                                        aria-labelledby='Email Domain'
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid container justifyContent={"center"} alignItems={"center"}>
                                                <Grid item xs={5} className={classes.gridPaddingTop}>
                                                    <FormControl fullWidth>
                                                        <Button
                                                            variant='outlined'
                                                            disabled={!isEmailDomainValid}
                                                            onClick={() => { setEmailDomain(""); handleAddEmailDomain(); }}
                                                            aria-label='Add email domain'
                                                        >
                                                            Add Domain</Button>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                            <Grid container alignItems={"center"} justifyContent={"center"}>
                                                <Grid item xs={10} padding={2}>
                                                    {tempDomainList.length === 0 || <Paper variant='outlined'> <List className={classes.list} >
                                                        {tempDomainList.map((list, index) => (
                                                            <ListItem key={index}
                                                                aria-labelledby={list}
                                                                secondaryAction={
                                                                    <IconButton
                                                                        edge="end"
                                                                        aria-label="delete"
                                                                        onClick={() => { handleDeleteEmailDomain(index); }}
                                                                    >
                                                                        <CloseIcon />
                                                                    </IconButton>
                                                                }>
                                                                <ListItemText primary={list} />
                                                            </ListItem>
                                                        ))}
                                                    </List></Paper>
                                                    }
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Paper>
                                <Grid container alignItems={"center"} justifyContent={"center"} className={classes.subWrapper3}>
                                    <Grid item xs={6} alignSelf={"center"} className={classes.btnContainer}  >
                                        <Grid container direction={"column"}>
                                            <Grid item xs={4}>
                                                <FormControl fullWidth>
                                                    <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid} fullWidth aria-labelledby='submit button'>
                                                        {selectedSchool.id > 0 ? "Update School" : "Create School"}
                                                    </Button>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Dialog >
        </div >
    );
}